// @ts-nocheck
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, ShieldX } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export function AccountCancellation() {
  const { user, signOut } = useAuth();
  const [cancelling, setCancelling] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');

  const handleDeleteAccount = async () => {
    if (confirmText !== 'DELETE') return;

    try {
      setCancelling(true);
      
      // Mark subscription as cancelled and save reason
      if (user) {
        const updateData: any = {
          status: 'cancelled',
          cancelled_at: new Date().toISOString()
        };
        
        if (cancellationReason.trim()) {
          updateData.cancellation_reason = cancellationReason.trim();
        }
        
        await supabase
          .from('subscriptions')
          .update(updateData)
          .eq('user_id', user.id)
          .eq('status', 'active'); // Only cancel active subscriptions
      }
      
      // Here you would implement account deletion logic
      // This might involve calling an edge function to properly clean up user data
      toast.success('Account deletion request submitted');
      
      // Sign out user
      await signOut();
      
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-destructive">Danger Zone</h1>
        <p className="text-muted-foreground">This action cannot be undone</p>
      </div>

      {/* Delete Account */}
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Delete Account
          </CardTitle>
          <CardDescription>
            We're sorry to see you go! Your feedback helps us improve RecruitLook.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-muted rounded-lg border">
            <p className="text-sm text-muted-foreground mb-3">
              We'd really appreciate knowing why you're leaving. Your feedback helps us improve the experience for all athletes.
            </p>
            <div className="space-y-2">
              <Label htmlFor="cancellation-reason">
                What's the main reason for cancelling? (Optional)
              </Label>
              <Textarea
                id="cancellation-reason"
                placeholder="e.g., Found another platform, Too expensive, Missing features I need, etc."
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                rows={4}
                className="resize-none"
              />
            </div>
          </div>

          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <h4 className="font-semibold text-destructive mb-2">⚠️ Warning</h4>
            <ul className="text-sm text-destructive/80 space-y-1">
              <li>• All your profile data will be permanently deleted</li>
              <li>• Your recruiting history and activities will be removed</li>
              <li>• Any active subscriptions will be cancelled</li>
              <li>• This action cannot be reversed</li>
            </ul>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-text">
              Type <strong>DELETE</strong> to confirm account deletion
            </Label>
            <Input
              id="confirm-text"
              placeholder="Type DELETE here"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
            />
          </div>

          <Button 
            variant="destructive" 
            onClick={handleDeleteAccount}
            disabled={confirmText !== 'DELETE' || cancelling}
            className="gap-2"
          >
            {cancelling ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            Delete Account Permanently
          </Button>
        </CardContent>
      </Card>

    </div>
  );
}